/**
 * Content of the administration item as entered by the user (pasted or typed in) and parsed by the frontend
 */
export interface ImportedItemRow<T> {
  /** Where in the source content the item was found */
  rowNumber: number;
  /** The content of the item as entered by the user and parsed to specific attributes  */
  inputData: Partial<T>;
  /** The raw data as entered by the user, without any parsing, split to cells */
  rawData: string[];
  /** True if there is an error in the data (invalid or missing values, duplicates) */
  isError?: boolean;
  /** List of errors in the data (if there are any) */
  errors?: string[];
  /** What an advisory rule noticed: shown, not blocking - the server decides once it has resolved the row */
  warnings?: string[];
  /** Specific attribute to indicate that the item is a duplicate of another item in the list. Duplicate item is also an error */
  isDuplicate?: boolean;
}

export enum ImportFormatDataType {
  STRING = 'string',
  NUMBER = 'number',
  DATE = 'date',
  BOOLEAN = 'boolean',
}

/**
 * Describes one attribute of the item to be imported
 */
export interface ImportFormatItem<T> {
  /** property of T */
  id: keyof T;
  /** Human-readable label of id */
  label: string;
  /** Data type of the attribute */
  dataType: ImportFormatDataType;
}

export type ImportedItemValidationResult = string[] | undefined;

export type ImportedItemValidatorFunction<T> = (
  item: ImportedItemRow<T>,
) => ImportedItemValidationResult;

export interface ImportedItemValidatorObject<T> {
  /** An advisory validator's findings are warnings: the row stays importable and the server decides. */
  readonly advisory?: boolean;

  validate(item: ImportedItemRow<T>): ImportedItemValidationResult;

  /** Reset the internal state of the validator, called before parsing starts */
  reset(): void;
}

export type ImportedItemValidator<T> =
  | ImportedItemValidatorFunction<T>
  | ImportedItemValidatorObject<T>;

type ImportValue = string | number | boolean | Date | null | undefined;

export type ImportableItem = Record<string, ImportValue>;

export type ImportedItemParserFunction<T extends ImportableItem> = (
  content: string,
  inputFormat: readonly ImportFormatItem<T>[],
  validators: readonly ImportedItemValidator<T>[],
  parser?: DOMParser,
) => readonly ImportedItemRow<T>[];

export interface ImportedItemParser<T extends ImportableItem> {
  contentType: string;
  parse: ImportedItemParserFunction<T>;
}

export type ValidatorTemplate = Record<string, 'number' | 'string'>;

export interface ImportedItemValidatorContext {
  id: string;
  pattern: RegExp;
  errorMessage: string;
}
